import { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import { activeRegionAtom } from "@/atoms/activeRegion";
import { useAuth } from "@/hooks/useAuth";
import {
  trackEngagementPromptDismissed,
  trackEngagementPromptShown,
} from "@/lib/analytics";
import {
  markPromptConverted,
  markPromptDismissed,
  recordRegionView,
} from "@/lib/engagementPrompt";
import AuthPromptDialog from "./AuthPromptDialog";

/**
 * Singleton mounted at the router root. Watches the active region atom
 * and opens an `AuthPromptDialog` once an anonymous visitor has viewed
 * enough unique regions to be considered engaged. See
 * `src/lib/engagementPrompt.ts` for the threshold / cooldown logic.
 */
export default function EngagementAuthPrompt() {
  const { user } = useAuth();
  const activeRegion = useRecoilValue(activeRegionAtom);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (user) markPromptConverted();
  }, [user]);

  useEffect(() => {
    if (user || !activeRegion) return;
    if (recordRegionView(activeRegion).shouldPrompt) {
      trackEngagementPromptShown();
      setOpen(true);
    }
  }, [activeRegion, user]);

  const handleClose = () => {
    markPromptDismissed();
    trackEngagementPromptDismissed();
    setOpen(false);
  };

  return (
    <AuthPromptDialog
      open={open}
      onClose={handleClose}
      source="engagement"
      reason="Enjoying the forecasts? Sign in to save your home spot, post reports, and vote on what others are seeing."
    />
  );
}
